import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  Storage as StorageIcon,
  UnfoldLess as CollapseAllIcon,
  Info as InfoIcon,
  Summarize as SummarizeIcon,
} from '@mui/icons-material';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { PERMISSIONS } from '@/common-constants/permissions';
import { transactionService, s3Service } from '@/services/api';
import type { TransactionDetail } from '@/common-types';
import ParityBar from '@/common-components/parity-bar/parity-bar';
import {
  getLogsForStep,
  formatActionName,
  formatTimestampWithZones,
  getOrgDisplay,
  getTimeline,
  getSuccessfulSteps,
  getFailedSteps,
  getOrgParity,
} from './transaction-lookup-utils';

export default function TransactionLookup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const itParam = searchParams.get('it') ?? '';
  const { hasPermission } = useAuthentication();
  const canAccessLogOracle = hasPermission(PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE);
  const canViewJobDetails = hasPermission(PERMISSIONS.JOBS_IN_LOG_ORACLE);
  const canAccessAiChat = hasPermission(PERMISSIONS.AI_CHAT_IN_LOG_ORACLE);
  const canReadS3Objects = hasPermission(PERMISSIONS.READ_S3_OBJECTS);

  const [transactionId, setTransactionId] = useState(itParam ?? '');
  const [data, setData] = useState<TransactionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStepExpand = (stepKey: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepKey)) next.delete(stepKey);
      else next.add(stepKey);
      return next;
    });
  };

  const [s3RootPrefix, setS3RootPrefix] = useState('');
  const [s3Tree, setS3Tree] = useState<Record<string, string[]>>({});
  const [s3Expanded, setS3Expanded] = useState<Set<string>>(new Set());
  const [s3LoadingPrefixes, setS3LoadingPrefixes] = useState<Set<string>>(new Set());
  const [s3Error, setS3Error] = useState<string | null>(null);
  const [s3BrowseStarted, setS3BrowseStarted] = useState(false);
  const [s3Downloading, setS3Downloading] = useState<Set<string>>(new Set());
  const s3DownloadLockRef = useRef(false);

  const fetchS3Tree = useCallback(async (prefix: string) => {
    const region = data?.deploymentRegion ?? 'us-west-2';
    setS3LoadingPrefixes((prev) => new Set(prev).add(prefix));
    try {
      const result = await s3Service.listDirectory('ember', region, prefix);
      setS3Tree((prev) => ({ ...prev, [prefix]: result.paths ?? [] }));
      setS3Expanded((prev) => new Set(prev).add(prefix));
    } catch (err) {
      setS3Error(err instanceof Error ? err.message : 'Failed to list directory');
    } finally {
      setS3LoadingPrefixes((prev) => { const next = new Set(prev); next.delete(prefix); return next; });
    }
  }, [data?.deploymentRegion]);

  const handleS3ToggleFolder = (fullPrefix: string) => {
    if (s3LoadingPrefixes.size > 0) return; // only one list-directory at a time
    if (s3Expanded.has(fullPrefix)) {
      setS3Expanded((prev) => { const next = new Set(prev); next.delete(fullPrefix); return next; });
    } else if (s3Tree[fullPrefix]) {
      setS3Expanded((prev) => new Set(prev).add(fullPrefix));
    } else {
      fetchS3Tree(fullPrefix);
    }
  };

  const handleS3FileClick = (fullPath: string) => {
    if (s3DownloadLockRef.current || s3Downloading.size > 0) return;
    const region = data?.deploymentRegion ?? 'us-west-2';
    s3DownloadLockRef.current = true;
    setS3Downloading((prev) => new Set(prev).add(fullPath));
    s3Service
      .readObjects('ember', region, [fullPath])
      .then((res) => {
        const entry = res.preSignedUrls?.[0];
        if (entry?.url) window.open(entry.url, '_blank', 'noopener,noreferrer');
        if (entry?.error) setS3Error(entry.error);
      })
      .catch((err) => setS3Error(err instanceof Error ? err.message : 'Download failed'))
      .finally(() => {
        s3DownloadLockRef.current = false;
        setS3Downloading((prev) => { const next = new Set(prev); next.delete(fullPath); return next; });
      });
  };

  const handleS3CollapseAll = () => {
    setS3Expanded(new Set());
  };

  const handleS3Browse = () => {
    if (s3LoadingPrefixes.size > 0) return; // only one list-directory at a time
    setS3BrowseStarted(true);
    fetchS3Tree(s3RootPrefix);
  };

  const fetchTransaction = useCallback(async (id: string) => {
    if (!id.trim()) return;
    setError(null);
    setLoading(true);
    setS3RootPrefix('');
    setS3Tree({});
    setS3Expanded(new Set());
    setS3LoadingPrefixes(new Set());
    setS3Error(null);
    setS3BrowseStarted(false);
    setDownloadError(null);
    try {
      const result = await transactionService.getTransactionById(id.trim());
      const resolvedId = result?.transactionId ?? id.trim();
      setData(result ?? null);
      setTransactionId(resolvedId);
      setSearchParams({ it: resolvedId }, { replace: true });

      const s3Key = result?.jobDetailsS3ObjectKey;
      if (typeof s3Key === 'string' && s3Key.includes('/')) {
        const parts = s3Key.replace(/\/$/, '').split('/');
        parts.pop(); // remove filename
        parts.pop(); // remove parent dir (e.g. "stdout")
        const root = parts.length > 0 ? parts.join('/') + '/' : '';
        if (root) setS3RootPrefix(root);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch transaction');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  useEffect(() => {
    if (!canAccessLogOracle) return;
    const id = itParam?.trim();
    if (id) {
      setTransactionId(id);
      fetchTransaction(id);
    }
  }, [itParam]); // eslint-disable-line react-hooks/exhaustive-deps -- run when ?it= is present

  const handleFetch = () => fetchTransaction(transactionId);
  const handleClear = () => {
    setTransactionId('');
    setData(null);
    setError(null);
    setSearchParams({});
  };
  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(transactionId);
    } catch {
      // ignore
    }
  };

  const sourceOrg = data ? getOrgDisplay(data, 'sourceOrg') : null;
  const destOrg = data ? getOrgDisplay(data, 'destinationOrg') : null;
  const timeline = data ? getTimeline(data) : {};
  const successfulSteps = data ? getSuccessfulSteps(data) : [];
  const failedSteps = data ? getFailedSteps(data) : [];
  const autoDetectedLogs = data?.autoDetectedLogs?.logs ?? [];
  const orgParity = data ? getOrgParity(data) : null;
  const startedTimestamp = formatTimestampWithZones(timeline.started);
  const finishedTimestamp = formatTimestampWithZones(timeline.finished);
  const status = data?.status ?? '—';
  const isFailed = String(status).toUpperCase() === 'FAILED';
  const parityCopied = data?.totalMatchingResourcesOnDestOrg ?? (orgParity != null ? Number(orgParity) : (data?.orgParity != null ? Number(data.orgParity) : 0));
  const parityTotal = data?.totalResourcesOnSourceOrg ?? (orgParity != null || data?.orgParity != null ? 100 : 0);
  const isRunning = String(status).toLowerCase() === 'started';

  if (!canAccessLogOracle) {
    return (
      <Box>
        <Alert severity="warning">
          You need permission to view Log Oracle to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      {/* Search */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}>
        <CardContent sx={{ py: 2, px: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} useFlexGap>
            <TextField
              label="Transaction ID"
              size="small"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFetch()}
              placeholder="e.g. 8996ab19-5596-45a4-95e4-8413b16e0734"
              fullWidth
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 220 },
                '& .MuiOutlinedInput-root': { borderRadius: 1.25, bgcolor: 'grey.50', fontSize: '0.875rem' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <IconButton size="small" onClick={handleClear} aria-label="Clear" sx={{ mr: 0.25 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy ID">
                      <IconButton size="small" onClick={handleCopyId} disabled={!transactionId} aria-label="Copy">
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="medium"
              onClick={handleFetch}
              disabled={loading || !transactionId.trim()}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              sx={{ borderRadius: 1.25, px: 2.5, textTransform: 'none', fontWeight: 600, boxShadow: (t) => `0 1px 4px ${alpha(t.palette.primary.main, 0.3)}` }}
            >
              {loading ? 'Fetching…' : 'Fetch'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {downloadError && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {data && (
        <Grid container spacing={2}>
          {/* Row 1: Summary (left) + Source Org + Dest Org */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                bgcolor: isFailed ? (t) => alpha(t.palette.error.main, 0.04) : (t) => alpha(t.palette.success.main, 0.06),
                borderLeft: 4,
                borderLeftColor: isFailed ? 'error.main' : 'success.main',
              }}
            >
              <CardContent sx={{ py: 2, px: 2.5 }}>
                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2} sx={{ mb: 1.5 }}>
                  {(orgParity != null || data.orgParity != null) && parityTotal > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Parity</Typography>
                      <ParityBar
                        copied={typeof parityCopied === 'number' ? parityCopied : Number(parityCopied) || 0}
                        total={parityTotal}
                        isRunning={isRunning}
                        totalBlocks={10}
                        showLoaderWhenRunning={false}
                      />
                    </Box>
                  )}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>Transaction ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{data.transactionId ?? transactionId}</Typography>
                  </Grid>
                  {data.action && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>Action</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{formatActionName(data.action)}</Typography>
                    </Grid>
                  )}
                  {data.initiatedBy && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>Initiated by</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.initiatedBy}</Typography>
                    </Grid>
                  )}
                  {timeline.started && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>Started</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>Local: {startedTimestamp.local}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">UTC: {startedTimestamp.utc}</Typography>
                    </Grid>
                  )}
                  {timeline.finished && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>Finished</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>Local: {finishedTimestamp.local}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">UTC: {finishedTimestamp.utc}</Typography>
                    </Grid>
                  )}
                  {(canViewJobDetails || canAccessAiChat) && data.transactionId && (
                    <Grid size={{ xs: 12 }} sx={{ pt: 1 }}>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {canViewJobDetails && (
                          <Button
                            component={Link}
                            to={`/log-oracle/transaction-lookup/${data.transactionId}`}
                            size="small"
                            variant="outlined"
                            startIcon={<InfoIcon sx={{ fontSize: 18 }} />}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderRadius: 1.25 }}
                          >
                            Job details
                          </Button>
                        )}
                        {canAccessAiChat && (
                          <Button
                            component={Link}
                            to="/log-oracle/ai-chat?jobSummary=1"
                            state={{ jobSummaryTransactionId: data.transactionId }}
                            size="small"
                            variant="outlined"
                            startIcon={<SummarizeIcon sx={{ fontSize: 18 }} />}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderRadius: 1.25 }}
                          >
                            AI Summary
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {sourceOrg && (
            <Grid size={{ xs: 12, md: 6, lg: 3.5 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>Source organization</Typography>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Name</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, fontSize: '0.8125rem' }}>{sourceOrg.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.75 }}>{sourceOrg.id}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Region</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{sourceOrg.region}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {destOrg && (
            <Grid size={{ xs: 12, md: 6, lg: 3.5 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>Destination organization</Typography>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Name</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, fontSize: '0.8125rem' }}>{destOrg.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.75 }}>{destOrg.id}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>Region</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{destOrg.region}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Row 3: Steps */}
          {(successfulSteps.length > 0 || failedSteps.length > 0) && (
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                    <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>Steps</Typography>
                    <Typography variant="caption" color="text.secondary">{successfulSteps.length} passed, {failedSteps.length} failed</Typography>
                  </Stack>
                  <Divider sx={{ mb: 1 }} />
                  <Grid container spacing={1}>
                    {(() => {
                      const allSteps = [...successfulSteps.map((name) => ({ name, success: true })), ...failedSteps.map((name) => ({ name, success: false }))];
                      const mid = Math.ceil(allSteps.length / 2);
                      const leftCol = allSteps.slice(0, mid);
                      const rightCol = allSteps.slice(mid);
                      return (
                        <>
                          <Grid size={{ xs: 12, md: 6 }}>
                            {leftCol.map((item, i) => {
                              const stepKey = `l-${i}`;
                              const stepLogs = getLogsForStep(item.name, autoDetectedLogs);
                              const isExpanded = expandedSteps.has(stepKey);
                              return (
                                <Box key={stepKey} sx={{ borderBottom: i < leftCol.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                                  <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.75 }}>
                                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.200', color: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, fontSize: '0.75rem' }}>{i + 1}</Box>
                                    {item.success ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} /> : <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem', flex: 1, minWidth: 0 }}>{item.name}</Typography>
                                    <Chip size="small" label={item.success ? 'Passed' : 'Failed'} color={item.success ? 'success' : 'error'} sx={{ fontWeight: 600, fontSize: '0.625rem', height: 20 }} />
                                    {stepLogs.length > 0 && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); toggleStepExpand(stepKey); }}
                                        endIcon={isExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.675rem', minWidth: 0, px: 1.25, py: 0.25, borderRadius: 1.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                                      >
                                        Download Logs
                                      </Button>
                                    )}
                                  </Stack>
                                  <Collapse in={isExpanded} timeout={150} unmountOnExit>
                                    <Box sx={{ ml: 5, mb: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.04), borderRadius: 1.5, border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.12), px: 1.5, py: 1 }}>
                                      <Stack spacing={0.25}>
                                        {stepLogs.map((log) => (
                                            <Stack key={log.fileName} direction="row" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.725rem', flex: 1, minWidth: 0 }}>{log.fileName}</Typography>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e: React.MouseEvent) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  window.open(log.url, '_blank', 'noopener,noreferrer');
                                                }}
                                                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                sx={{
                                                  textTransform: 'none', fontWeight: 600, fontSize: '0.65rem', minWidth: 0, px: 1.25, py: 0.125, borderRadius: 1, flexShrink: 0, whiteSpace: 'nowrap',
                                                }}
                                              >
                                                Download
                                              </Button>
                                            </Stack>
                                          ))}
                                      </Stack>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            {rightCol.map((item, i) => {
                              const stepKey = `r-${i}`;
                              const stepLogs = getLogsForStep(item.name, autoDetectedLogs);
                              const isExpanded = expandedSteps.has(stepKey);
                              return (
                                <Box key={stepKey} sx={{ borderBottom: i < rightCol.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                                  <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.75 }}>
                                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.200', color: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, fontSize: '0.75rem' }}>{mid + i + 1}</Box>
                                    {item.success ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} /> : <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem', flex: 1, minWidth: 0 }}>{item.name}</Typography>
                                    <Chip size="small" label={item.success ? 'Passed' : 'Failed'} color={item.success ? 'success' : 'error'} sx={{ fontWeight: 600, fontSize: '0.625rem', height: 20 }} />
                                    {stepLogs.length > 0 && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); toggleStepExpand(stepKey); }}
                                        endIcon={isExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.675rem', minWidth: 0, px: 1.25, py: 0.25, borderRadius: 1.25, whiteSpace: 'nowrap', flexShrink: 0 }}
                                      >
                                        Download Logs
                                      </Button>
                                    )}
                                  </Stack>
                                  <Collapse in={isExpanded} timeout={150} unmountOnExit>
                                    <Box sx={{ ml: 5, mb: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.04), borderRadius: 1.5, border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.12), px: 1.5, py: 1 }}>
                                      <Stack spacing={0.25}>
                                        {stepLogs.map((log) => (
                                            <Stack key={log.fileName} direction="row" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.725rem', flex: 1, minWidth: 0 }}>{log.fileName}</Typography>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e: React.MouseEvent) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  window.open(log.url, '_blank', 'noopener,noreferrer');
                                                }}
                                                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                sx={{
                                                  textTransform: 'none', fontWeight: 600, fontSize: '0.65rem', minWidth: 0, px: 1.25, py: 0.125, borderRadius: 1, flexShrink: 0, whiteSpace: 'nowrap',
                                                }}
                                              >
                                                Download
                                              </Button>
                                            </Stack>
                                          ))}
                                      </Stack>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Grid>
                        </>
                      );
                    })()}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Row 4: S3 File Browser */}
          {s3RootPrefix && canReadS3Objects && (
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: s3BrowseStarted ? 1.5 : 0 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <StorageIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>S3 Logs Files</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {s3BrowseStarted && s3Expanded.size > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleS3CollapseAll}
                          startIcon={<CollapseAllIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.725rem', borderRadius: 1.25 }}
                        >
                          Collapse All
                        </Button>
                      )}
                      {!s3BrowseStarted && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleS3Browse}
                          disabled={s3LoadingPrefixes.size > 0}
                          startIcon={s3LoadingPrefixes.size > 0 ? <CircularProgress size={16} color="inherit" /> : <FolderIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.725rem', borderRadius: 1.25, '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' } }}
                        >
                          {s3LoadingPrefixes.size > 0 ? 'Loading…' : 'Browse Files'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  {s3Error && (
                    <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }} onClose={() => setS3Error(null)}>
                      {s3Error}
                    </Alert>
                  )}

                  {s3BrowseStarted && (
                    <>
                      <Divider sx={{ mb: 1 }} />
                      {(() => {
                        const renderTree = (prefix: string, depth: number): React.ReactNode => {
                          const children = s3Tree[prefix];
                          const isLoading = s3LoadingPrefixes.has(prefix);
                          if (isLoading) {
                            return <LinearProgress key={`loading-${prefix}`} sx={{ ml: depth * 2.5, my: 0.5, borderRadius: 1, maxWidth: 200 }} />;
                          }
                          if (!children) return null;
                          const anyDirLoading = s3LoadingPrefixes.size > 0;
                          return children.map((name) => {
                            const isFolder = name.endsWith('/');
                            const fullPath = prefix + name;
                            const isExpanded = s3Expanded.has(fullPath);
                            const isFolderLoading = s3LoadingPrefixes.has(fullPath);
                            const isFileDownloading = !isFolder && s3Downloading.has(fullPath);
                            const folderDisabled = isFolder && anyDirLoading && !isFolderLoading;
                            return (
                              <Box key={fullPath}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                  onClick={
                                    isFolder
                                      ? folderDisabled
                                        ? (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }
                                        : () => handleS3ToggleFolder(fullPath)
                                      : undefined
                                  }
                                  sx={{
                                    py: 0.5,
                                    px: 1,
                                    pl: depth * 2.5 + 1,
                                    borderRadius: 1,
                                    cursor: isFolder && !folderDisabled ? 'pointer' : folderDisabled ? 'not-allowed' : 'default',
                                    opacity: folderDisabled ? 0.6 : 1,
                                    pointerEvents: folderDisabled ? 'auto' : undefined,
                                    ...(folderDisabled ? {} : { '&:hover': { bgcolor: (t: { palette: { primary: { main: string } } }) => alpha(t.palette.primary.main, 0.06) } }),
                                    transition: 'background-color 0.15s',
                                  }}
                                >
                                  {isFolder
                                    ? (isExpanded
                                      ? <FolderOpenIcon sx={{ fontSize: 20, color: 'warning.main', flexShrink: 0 }} />
                                      : <FolderIcon sx={{ fontSize: 20, color: 'warning.main', flexShrink: 0 }} />)
                                    : <FileIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />}
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: isFolder ? 600 : 400, flex: 1, minWidth: 0 }}>
                                    {name.replace(/\/$/, '')}
                                  </Typography>
                                  {isFolder && (
                                    isFolderLoading
                                      ? <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                                      : (
                                        <IconButton
                                          size="small"
                                          sx={{ flexShrink: 0, p: 0.25, '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' } }}
                                          disabled={folderDisabled}
                                          tabIndex={folderDisabled ? -1 : 0}
                                        >
                                          {isExpanded
                                            ? <ExpandLessIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                            : <ExpandMoreIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                                        </IconButton>
                                      )
                                  )}
                                  {!isFolder && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (anyDirLoading || s3Downloading.size > 0) return; handleS3FileClick(fullPath); }}
                                      disabled={isFileDownloading || anyDirLoading || s3Downloading.size > 0}
                                      startIcon={isFileDownloading ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
                                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.65rem', minWidth: 0, px: 1.25, py: 0.125, borderRadius: 1, flexShrink: 0, whiteSpace: 'nowrap', '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' } }}
                                    >
                                      Download
                                    </Button>
                                  )}
                                </Stack>
                                {isFolder && isExpanded && renderTree(fullPath, depth + 1)}
                              </Box>
                            );
                          });
                        };
                        return renderTree(s3RootPrefix, 0);
                      })()}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
}
